import { useState } from 'react'
import { Code2, GitBranch, Zap, CheckCircle2, Users, Clock, FileText, ArrowRight } from 'lucide-react'

export default function VersionControlPage() {
  const [stats] = useState({
    versions: 4847,
    branches: 234,
    commits: 12456,
    conflicts: 12,
  })

  const features = [
    { title: '📝 Version Control', description: 'Track all document versions with detailed history', icon: Code2, color: 'from-blue-600 to-blue-500', stats: '4,847 Versions' },
    { title: '🌳 Branch Management', description: 'Create branches for parallel document editing', icon: GitBranch, color: 'from-green-600 to-green-500', stats: '234 Active Branches' },
    { title: '⚡ Merge & Conflict Resolution', description: 'Intelligent merging with conflict detection', icon: Zap, color: 'from-yellow-600 to-yellow-500', stats: '12 Conflicts/Month' },
    { title: '✓ Diff & Compare', description: 'See detailed differences between versions', icon: CheckCircle2, color: 'from-purple-600 to-purple-500', stats: 'Line-level Diffs' },
    { title: '👥 Collaboration Tracking', description: 'Track contributions by team member', icon: Users, color: 'from-cyan-600 to-cyan-500', stats: '234 Contributors' },
    { title: '⏰ Timeline View', description: 'Visual timeline of all changes', icon: Clock, color: 'from-indigo-600 to-indigo-500', stats: 'Interactive Timeline' },
    { title: '📋 Change Log', description: 'Auto-generated change logs and release notes', icon: FileText, color: 'from-pink-600 to-pink-500', stats: 'Auto-Generated' },
    { title: '🔄 Rollback Support', description: 'Easily revert to any previous version', icon: Zap, color: 'from-red-600 to-red-500', stats: 'One-Click Rollback' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-8 text-white shadow-2xl border border-green-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Code2 className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Version Control System</h1>
                </div>
                <p className="text-green-100 text-lg font-semibold">Document Version Control & Collaboration</p>
                <p className="text-green-200 text-sm mt-1">Git-like versioning for documents • Branching • Merging • Conflict Resolution</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.versions}</div>
                <div className="text-sm text-green-100">Total Versions</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Versions', value: stats.versions, icon: FileText, color: 'from-blue-600 to-blue-500' },
            { label: 'Active Branches', value: stats.branches, icon: GitBranch, color: 'from-green-600 to-green-500' },
            { label: 'Total Commits', value: stats.commits, icon: Zap, color: 'from-purple-600 to-purple-500' },
            { label: 'Monthly Conflicts', value: stats.conflicts, icon: AlertTriangle, color: 'from-red-600 to-red-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Version Control Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div key={idx} className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1">
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>
                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
